import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCanActAsManager } from './useCanActAsManager';
import { ACTIVE_STAGES } from '@/constants/dealStages';

type MobileHomeStats = {
  activeDeals: number;
  pendingTasks: number;
  unreadMessages: number;
};

async function fetchMobileHomeStats(appUserId: string): Promise<MobileHomeStats> {
  const pendingStatuses = ['pending', 'in_progress', 'blocked'];

  const [dealsResult, tasksResult, messagesResult] = await Promise.all([
    supabase
      .from('deals')
      .select('id', { count: 'exact', head: true })
      .in('stage', ACTIVE_STAGES),
    supabase
      .from('tasks')
      .select('id', { count: 'exact', head: true })
      .in('status', pendingStatuses),
    supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('owner_session_id', appUserId)
      .eq('direction', 'inbound')
      .is('read_at', null),
  ]);

  if (dealsResult.error) throw dealsResult.error;
  if (tasksResult.error) throw tasksResult.error;
  if (messagesResult.error) throw messagesResult.error;

  return {
    activeDeals: dealsResult.count ?? 0,
    pendingTasks: tasksResult.count ?? 0,
    unreadMessages: messagesResult.count ?? 0,
  };
}

export function useMobileHomeStats() {
  const { appUserId } = useCanActAsManager();

  return useQuery({
    queryKey: ['mobile-home-stats', appUserId],
    queryFn: () => fetchMobileHomeStats(appUserId!),
    enabled: !!appUserId,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  });
}

